import React, { useEffect, useRef } from 'react';
import { Box, Button } from '@mui/material';
import styles from './DocumentContent.module.scss';
import { DocumentContentProps } from './types';
import { TemplatePlaceholdersBlock } from './components/TemplatePlaceholdersBlock';
import { ProgressOverlay } from '../../../../common/progress-overlay/ProgressOverlay';
import { ActionButtons } from '../action-buttons/ActionButtons';
import ConfirmModal from '../../../../common/confirm-modal/ConfirmModal';
import JoditEditor from 'jodit-react';
import { useDocumentContent } from '../../hooks/useDocumentContent';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const DocumentContent = ({
  html,
  pendingStatus,
  downloadDocument,
  generateAISummary,
  signDocument,
  editDocument,
  createTemplate,
  deleteDocument,
  saveDocument,
  setDocumentHTML,
  contentEditable,
  setContentEditable,
  doc,
  templateCreationEnabled,
  templatePlaceholders,
  createTemplateFromDoc,
  goBack,
  showConfirmationModal,
  setShowConfirmationModal,
  documentHTMLId,
  deletePlaceholder,
  addTemplatePlaceholderRow,
}: DocumentContentProps) => {
  const navigate = useNavigate();
  const { fileBlobURL, fileLoading, creatingDocumentBlob, pdfBlobMode, setPDFBlobMode } =
    useDocumentContent(doc?.id, doc && doc.du_filetype ? doc.du_filetype[0] : null);
  const editor = useRef(null);
  const contentBlockRef = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslation();
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        contentEditable &&
        contentBlockRef.current &&
        !contentBlockRef.current.contains(event.target as Node) /*&&*/
        //(!popperNode || !popperNode.contains(event.target as Node)) // Ahora ignora correctamente los clics en el menú
      ) {
        const targetElement = event.target as HTMLElement;
        if (event.target instanceof HTMLElement) {
          const ignoredElements = ['BUTTON', 'INPUT', 'TEXTAREA', 'SELECT', 'LABEL', 'MENU-ITEM'];
          // if (ignoredElements.includes(event.target.tagName)) return;
        }

        /* if (
            targetElement.closest('.MuiMenuItem-root') || // Ignore clicks on MenuItem
            targetElement.closest('.MuiSelect-root') // Ignore clicks on Select
        ) {
          return;
        }*/
        setTimeout(() => {
          //setShowConfirmationModal(true);
        }, 0);
      }
    };

    //if (contentEditable) document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [contentEditable]);

  return (
    <>
      <Box className={styles.container} ref={contentBlockRef}>
        <Box className={styles.topBar}>
          <Box className={styles.headerActions}>
            {doc && 'du_filetype' in doc && doc.du_filetype[0] === 'pdf' && html && (
              <Button
                type='button'
                className='btn-default btn-action-basic btn-small'
                role='button'
                variant='contained'
                onClick={() => {
                  pdfBlobMode
                    ? (() => {
                        setPDFBlobMode(false);
                        //setContentEditable(true);
                        //editDocument();
                      })()
                    : (() => {
                        setPDFBlobMode(true);
                        setContentEditable(false);
                        navigate(window.location.pathname + '?initialTab=1');
                      })();
                }}
                style={{ marginRight: '8px' }}
              >
                {pdfBlobMode ? t('buttons.viewLatestVersion') : t('buttons.viewOriginalPDF')}
              </Button>
            )}
            <ActionButtons
              handleSaveDocument={saveDocument}
              handleAISummaryGeneration={generateAISummary}
              handleDocumentDownload={downloadDocument}
              handleSignDocument={signDocument}
              handleEditDocument={() => {
                editDocument();
                setPDFBlobMode(false);
              }}
              handleCreateTemplate={
                doc?.contract_generation
                  ? null
                  : () => {
                      createTemplate();
                      setPDFBlobMode(false);
                    }
              }
              handleDeleteDocument={deleteDocument}
              buttonSaveDisabled={!contentEditable || pdfBlobMode}
              handleCreateTemplateMainBtn={true}
              creatingTemplateEnabled={templateCreationEnabled}
              doc={doc}
              isDocumentContentTab={true}
            />
          </Box>
        </Box>
        <Box className={styles.mainContent}>
          {templateCreationEnabled && (
            <TemplatePlaceholdersBlock
              templatePlaceholders={templatePlaceholders}
              createTemplate={createTemplateFromDoc}
              goBack={goBack}
              documentHTMLId={documentHTMLId}
              deletePlaceholder={deletePlaceholder}
              addRow={addTemplatePlaceholderRow}
            />
          )}
          <div className={`${styles.main_container} ${templateCreationEnabled ? styles.w_50 : ''}`}>
            {doc && 'du_filetype' in doc && doc.du_filetype[0] === 'pdf' && pdfBlobMode && (
              // eslint-disable-next-line jsx-a11y/iframe-has-title
              <iframe src={fileBlobURL} className={styles.frame} sandbox={undefined} />
            )}
            <div
              className={
                'main-editor-container' +
                (doc &&
                'du_filetype' in doc &&
                doc.du_filetype[0] === 'pdf' &&
                (!html || pdfBlobMode)
                  ? ' hidden'
                  : '')
              }
            >
              <JoditEditor
                ref={editor}
                config={{
                  readonly: !contentEditable,
                  buttons:
                    'bold,italic,underline,strikethrough,eraser,ul,ol,font,fontsize,paragraph,lineHeight,copy,source,left,right,center,image,paste,selectall,hr,table,link,symbols,indent,print,outdent',
                }}
                value={html}
                onBlur={(newContent) => setDocumentHTML(newContent)} // preferred to use only this option to update the content for performance reasons
              />
            </div>
          </div>
        </Box>
        {showConfirmationModal && (
          <ConfirmModal
            open={showConfirmationModal}
            onClose={() => {
              saveDocument();
              setShowConfirmationModal(false);
            }}
            onConfirm={() => {
              setContentEditable(false);
              setShowConfirmationModal(false);
            }}
          />
        )}
      </Box>
      {fileLoading && <ProgressOverlay className='position-absolute' />}
    </>
  );
};

export default DocumentContent;
